import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Briefcase,
  Plus,
  Search,
  Filter,
  RotateCcw,
  Edit2,
  Trash2,
  MapPin,
  Calendar,
  IndianRupee,
  Building2,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  TrendingUp,
  AlertCircle,
  Eye,
} from 'lucide-react';
import { formatMoney, isAdmin } from '@vns/core';
import {
  useProjects,
  useCreateProject,
  useUpdateProject,
  useDeleteProject,
  useClientOptions,
  useProjectOptions,
  type NewProject,
  type ProjectStatus,
  type ProjectRow,
} from '../lib/queries';
import {
  useAllProjectsLatestDrawings,
  type ProjectDrawingRow,
  type DrawingCategory,
} from '../lib/project-drawings';
import DrawingViewerModal from '../components/DrawingViewerModal';
import DrawingUploadDrawer from '../components/DrawingUploadDrawer';
import ElevationUploadDrawer from '../components/ElevationUploadDrawer';
import { useSession } from '../lib/session';
import Shell from '../components/Shell';
import Confirm, { useConfirm } from '../components/Confirm';
import { useRestore } from '../lib/restore';
import {
  Drawer,
  Field,
  StatusChip,
  EmptyState,
  TableSkeleton,
  ErrorState,
  formatDate,
  useToast,
  Select,
} from '../components/ui';

const STATUSES: Array<{ value: ProjectStatus; label: string }> = [
  { value: 'planning', label: 'Planning' },
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
];

export default function Projects() {
  const navigate = useNavigate();
  const actor = useSession((s) => s.actor);
  const admin = isAdmin(actor);
  const [search, setSearch] = useState('');
  const [clientId, setClientId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [status, setStatus] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ProjectRow | null>(null);
  const [viewingDrawing, setViewingDrawing] = useState<{
    drawing: ProjectDrawingRow;
    allVersions?: ProjectDrawingRow[];
    projectName: string;
  } | null>(null);
  const [uploadingForProject, setUploadingForProject] = useState<{
    projectId: string;
    projectName: string;
    category?: DrawingCategory;
  } | null>(null);

  const confirm = useConfirm<ProjectRow>();
  const restore = useRestore();
  const remove = useDeleteProject();
  const toast = useToast();
  const { data: clients } = useClientOptions();
  const { data: projectOpts } = useProjectOptions(clientId || undefined);
  const { data, isLoading, error, refetch } = useProjects(search, status, clientId, projectId);
  const { data: latestDrawingsMap } = useAllProjectsLatestDrawings();

  async function doDelete() {
    if (!confirm.target) return;
    try {
      await remove.mutateAsync(confirm.target.id);
      const removed = confirm.target;
      toast.showWithAction(removed.name + ' removed', {
        label: 'Undo',
        run: () => restore.mutateAsync({ table: 'projects', id: removed.id }),
      });
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not remove the project', true);
    } finally {
      confirm.close();
    }
  }

  // Quick summary calculation for projects
  const totalCount = data?.length ?? 0;
  const ongoingCount = data?.filter((p) => p.status === 'ongoing').length ?? 0;
  const completedCount = data?.filter((p) => p.status === 'completed').length ?? 0;
  const totalValuePaise = data?.reduce((sum, p) => sum + p.valuePaise, 0) ?? 0;

  // Filter options
  const hasFilters = Boolean(search || clientId || projectId || status);

  const clientSelectOptions = [
    { value: '', label: 'All Clients' },
    ...(clients?.map((c) => ({ value: c.id, label: c.name })) ?? []),
  ];

  const projectSelectOptions = [
    { value: '', label: 'All Projects' },
    ...(projectOpts?.map((p) => ({ value: p.id, label: p.name })) ?? []),
  ];

  return (
    <Shell
      title="Projects"
      crumb="Directory"
      actions={
        admin && (
          <button
            className="btn btn-primary btn-auto btn-sm"
            onClick={() => setAdding(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
          >
            <Plus size={15} strokeWidth={2.2} />
            <span>Add Project</span>
          </button>
        )
      }
    >
      {/* Quick Summary Cards */}
      {!isLoading && data && (
        <motion.div
          className="kpi-row"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          style={{ marginBottom: 18 }}
        >
          <div className="kpi">
            <div className="lbl">
              <span>Total Projects</span>
              <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Briefcase size={14} />
              </div>
            </div>
            <div className="val">{totalCount}</div>
            <div className="sub">in company registry</div>
          </div>

          <div className="kpi">
            <div className="lbl">
              <span>Ongoing Sites</span>
              <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#ecfdf5', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Clock size={14} />
              </div>
            </div>
            <div className="val" style={{ color: '#059669' }}>{ongoingCount}</div>
            <div className="sub">actively under construction</div>
          </div>

          <div className="kpi">
            <div className="lbl">
              <span>Completed</span>
              <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#f8fafc', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CheckCircle2 size={14} />
              </div>
            </div>
            <div className="val">{completedCount}</div>
            <div className="sub">finished & handed over</div>
          </div>

          {admin && (
            <div className="kpi">
              <div className="lbl">
                <span>Total Portfolio Value</span>
                <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#f5f3ff', color: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <IndianRupee size={14} />
                </div>
              </div>
              <div className="val" style={{ color: 'var(--accent)' }}>
                {formatMoney(totalValuePaise)}
              </div>
              <div className="sub">cumulative contract sum</div>
            </div>
          )}
        </motion.div>
      )}

      {/* Modern Filter Toolbar */}
      <div className="toolbar projects-toolbar">
        <div className="field-inline projects-search-box">
          <Search size={14} style={{ color: 'var(--text-faint)' }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search projects by name, code, site location, client..."
            aria-label="Search projects"
          />
        </div>

        <div className="projects-filter-client">
          <Select
            value={clientId}
            onChange={(val) => {
              setClientId(val);
              setProjectId('');
            }}
            placeholder="All Clients"
            options={clientSelectOptions}
            searchable={clientSelectOptions.length > 7}
            aria-label="Filter by client"
          />
        </div>

        <div className="projects-filter-project">
          <Select
            value={projectId}
            onChange={(val) => setProjectId(val)}
            placeholder="All Projects"
            options={projectSelectOptions}
            searchable={projectSelectOptions.length > 7}
            aria-label="Filter by project"
          />
        </div>

        <div className="projects-filter-status">
          <Select
            value={status}
            onChange={(val) => setStatus(val)}
            placeholder="All Statuses"
            options={[
              { value: '', label: 'All Statuses' },
              ...STATUSES.map((s) => ({ value: s.value, label: s.label })),
            ]}
            aria-label="Filter by status"
          />
        </div>

        {hasFilters && (
          <button
            className="btn btn-auto btn-sm btn-ghost projects-reset-btn"
            onClick={() => {
              setSearch('');
              setClientId('');
              setProjectId('');
              setStatus('');
            }}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            aria-label="Reset all filters"
          >
            <RotateCcw size={13} />
            <span>Reset</span>
          </button>
        )}
      </div>

      {/* Projects Table Card */}
      <motion.div
        className="card"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="table-wrap">
          {isLoading ? (
            <TableSkeleton rows={5} cols={admin ? 7 : 4} />
          ) : error ? (
            <ErrorState error={error} onRetry={() => void refetch()} />
          ) : !data?.length ? (
            <EmptyState
              icon="briefcase"
              title={hasFilters ? 'No projects match your filters' : 'No projects yet'}
              hint={hasFilters ? 'Try clearing the filters above.' : 'Add a project to begin tracking site operations.'}
            />
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', paddingLeft: 24 }}>Project Name</th>
                  <th style={{ textAlign: 'left' }}>Client</th>
                  <th style={{ textAlign: 'left' }}>Location</th>
                  {/* Site staff never see project money */}
                  {admin && <th style={{ textAlign: 'left' }}>Total Contract</th>}
                  {admin && <th style={{ textAlign: 'left' }}>Collections Received</th>}
                  {admin && <th style={{ textAlign: 'left' }}>Pending Balance</th>}
                  <th style={{ textAlign: 'left' }}>Status</th>
                  <th style={{ textAlign: 'center' }}>Floor Plan</th>
                  <th style={{ textAlign: 'center' }}>Structural Details</th>
                  <th style={{ textAlign: 'center' }}>Working Drawing</th>
                  <th style={{ textAlign: 'center' }}>3D Elevation</th>
                  {admin && <th style={{ textAlign: 'right', paddingRight: 24 }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {data.map((p) => {
                  const drawings = latestDrawingsMap?.get(p.id);
                  const floorPlan = drawings?.floor_plan;
                  const structural = drawings?.structural_details;
                  const working = drawings?.working_drawing;
                  const elevation = drawings?.elevation;
                  const elevationCount = drawings?.elevationCount ?? 0;

                  return (
                    <tr
                      key={p.id}
                      className="clickable"
                      onClick={() => navigate(`/projects/${p.id}`)}
                    >
                      <td className="name" style={{ textAlign: 'left', paddingLeft: 24 }}>
                        <div style={{ fontWeight: 600, color: 'var(--text)' }}>{p.name}</div>
                        {p.startDate && (
                          <div className="meta" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 4 }}>
                            <Calendar size={11} />
                            <span>Started {formatDate(p.startDate)}</span>
                          </div>
                        )}
                      </td>
                      <td style={{ textAlign: 'left' }}>
                        <span style={{ color: 'var(--text-soft)', fontWeight: 500 }}>
                          {p.clientName}
                        </span>
                      </td>
                      <td style={{ textAlign: 'left' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-soft)' }}>
                          <MapPin size={12} style={{ color: 'var(--text-faint)' }} />
                          <span>{p.location ?? '—'}</span>
                        </div>
                      </td>
                      {admin && (
                        <td className="num" style={{ textAlign: 'left', fontWeight: 500 }}>
                          {formatMoney(p.valuePaise)}
                        </td>
                      )}
                      {admin && (
                        <td className="num" style={{ textAlign: 'left', color: '#059669', fontWeight: 600 }}>
                          <div>{formatMoney(p.paidPaise)}</div>
                          {p.paidPaise > p.valuePaise && (
                            <div style={{ fontSize: '11px', color: '#16a34a', fontWeight: 600 }}>
                              +{formatMoney(p.paidPaise - p.valuePaise)} extra
                            </div>
                          )}
                        </td>
                      )}
                      {admin && (
                        <td
                          className="num"
                          style={{
                            textAlign: 'left',
                            color: p.pendingPaise > 0 ? '#ea580c' : 'var(--text-faint)',
                            fontWeight: p.pendingPaise > 0 ? 600 : 400,
                          }}
                        >
                          {formatMoney(p.pendingPaise)}
                        </td>
                      )}
                      <td style={{ textAlign: 'left' }}>
                        <StatusChip value={p.status} />
                      </td>

                      {/* Floor Plan Eye Icon */}
                      <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        {floorPlan ? (
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => setViewingDrawing({ drawing: floorPlan, projectName: p.name })}
                            title={`View Floor Plan (v${floorPlan.version}: ${floorPlan.title})`}
                            style={{
                              width: 28,
                              height: 28,
                              margin: '0 auto',
                              color: '#2563eb',
                              background: '#eff6ff',
                              border: '1px solid #bfdbfe',
                            }}
                          >
                            <Eye size={13} />
                          </button>
                        ) : (
                          <span style={{ color: 'var(--text-faint)', fontSize: 13 }}>—</span>
                        )}
                      </td>

                      {/* Structural Details Eye Icon */}
                      <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        {structural ? (
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => setViewingDrawing({ drawing: structural, projectName: p.name })}
                            title={`View Structural Details (v${structural.version}: ${structural.title})`}
                            style={{
                              width: 28,
                              height: 28,
                              margin: '0 auto',
                              color: '#7c3aed',
                              background: '#f5f3ff',
                              border: '1px solid #ddd6fe',
                            }}
                          >
                            <Eye size={13} />
                          </button>
                        ) : (
                          <span style={{ color: 'var(--text-faint)', fontSize: 13 }}>—</span>
                        )}
                      </td>

                      {/* Working Drawing Eye Icon */}
                      <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        {working ? (
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => setViewingDrawing({ drawing: working, projectName: p.name })}
                            title={`View Working Drawing (v${working.version}: ${working.title})`}
                            style={{
                              width: 28,
                              height: 28,
                              margin: '0 auto',
                              color: '#059669',
                              background: '#ecfdf5',
                              border: '1px solid #a7f3d0',
                            }}
                          >
                            <Eye size={13} />
                          </button>
                        ) : (
                          <span style={{ color: 'var(--text-faint)', fontSize: 13 }}>—</span>
                        )}
                      </td>

                      {/* 3D Elevation Eye Icon */}
                      <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        {elevation ? (
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() =>
                              setViewingDrawing({
                                drawing: elevation,
                                allVersions: drawings?.elevations,
                                projectName: p.name,
                              })
                            }
                            title={`View 3D Elevation (${elevationCount} file${elevationCount > 1 ? 's' : ''}: ${elevation.title})`}
                            style={{
                              width: 28,
                              height: 28,
                              margin: '0 auto',
                              color: '#0284c7',
                              background: '#f0f9ff',
                              border: '1px solid #bae6fd',
                              position: 'relative',
                            }}
                          >
                            <Eye size={13} />
                            {elevationCount > 1 && (
                              <span
                                style={{
                                  position: 'absolute',
                                  top: -4,
                                  right: -4,
                                  background: '#0284c7',
                                  color: '#fff',
                                  fontSize: 9,
                                  fontWeight: 700,
                                  width: 14,
                                  height: 14,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  lineHeight: 1,
                                }}
                              >
                                {elevationCount}
                              </span>
                            )}
                          </button>
                        ) : (
                          <span style={{ color: 'var(--text-faint)', fontSize: 13 }}>—</span>
                        )}
                      </td>

                      {admin && (
                        <td style={{ textAlign: 'right', paddingRight: 24 }} onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                            <button
                              className="icon-btn"
                              onClick={() => setEditing(p)}
                              title={`Edit ${p.name}`}
                              aria-label={`Edit ${p.name}`}
                              style={{ width: 28, height: 28 }}
                            >
                              <Edit2 size={13} />
                            </button>
                            <button
                              className="icon-btn btn-danger"
                              onClick={() => confirm.ask(p)}
                              title={`Delete ${p.name}`}
                              aria-label={`Delete ${p.name}`}
                              style={{ width: 28, height: 28 }}
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </motion.div>

      {adding && <ProjectDrawer onClose={() => setAdding(false)} />}
      {editing && <ProjectDrawer existing={editing} onClose={() => setEditing(null)} />}

      {viewingDrawing && (
        <DrawingViewerModal
          initialDrawing={viewingDrawing.drawing}
          allVersions={viewingDrawing.allVersions}
          projectName={viewingDrawing.projectName}
          onClose={() => setViewingDrawing(null)}
          onUploadNewRevision={(category) => {
            const pId = viewingDrawing.drawing.projectId;
            const pName = viewingDrawing.projectName;
            setViewingDrawing(null);
            setUploadingForProject({ projectId: pId, projectName: pName, category });
          }}
        />
      )}

      {uploadingForProject && (
        uploadingForProject.category === 'elevation' ? (
          <ElevationUploadDrawer
            projectId={uploadingForProject.projectId}
            projectName={uploadingForProject.projectName}
            onClose={() => setUploadingForProject(null)}
          />
        ) : (
          <DrawingUploadDrawer
            projectId={uploadingForProject.projectId}
            projectName={uploadingForProject.projectName}
            initialCategory={uploadingForProject.category}
            onClose={() => setUploadingForProject(null)}
          />
        )
      )}

      {confirm.target && (
        <Confirm
          title={`Delete Project: "${confirm.target.name}"?`}
          message="Are you sure you want to permanently delete this project? Please read the disclaimer below before confirming."
          confirmLabel="Delete Everything"
          danger
          busy={remove.isPending}
          onConfirm={() => void doDelete()}
          onCancel={confirm.close}
        >
          <div
            style={{
              padding: '12px 14px',
              borderRadius: 'var(--radius-sm)',
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.25)',
              fontSize: 12.5,
              color: '#991b1b',
              lineHeight: 1.5,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>⚠️ Disclaimer & Cascade Deletion</span>
            </div>
            <div>
              Deleting this project will permanently remove it from the database and storage, including:
            </div>
            <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
              <li>All client payments, advances & receipts</li>
              <li>All material purchases, purchase bills & supplier records</li>
              <li>All subcontractor visits, labour batches & payments</li>
              <li>All site expenses, petty cash & vouchers</li>
              <li>All material consumption & stock transfer records</li>
              <li>All uploaded drawings, elevations & project files in storage</li>
            </ul>
            <div style={{ marginTop: 6, fontWeight: 600 }}>
              This action cannot be undone.
            </div>
          </div>
        </Confirm>
      )}
    </Shell>
  );
}

/* -------------------------- Add / Edit Project Drawer -------------------------- */

const EMPTY: NewProject = {
  client_id: '',
  name: '',
  location: '',
  start_date: '',
  status: 'ongoing',
  valueRupees: '',
  description: '',
};

export function ProjectDrawer({
  existing,
  defaultClientId,
  onClose,
}: {
  existing?: ProjectRow;
  defaultClientId?: string;
  onClose: () => void;
}) {
  const [form, setForm] = useState<NewProject>(
    existing
      ? {
          client_id: existing.clientId,
          name: existing.name,
          location: existing.location ?? '',
          start_date: existing.startDate ?? '',
          status: existing.status,
          valueRupees: String(existing.valuePaise / 100),
          description: '',
        }
      : { ...EMPTY, client_id: defaultClientId ?? '' },
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { data: clients, isLoading: clientsLoading } = useClientOptions();
  const create = useCreateProject();
  const update = useUpdateProject();
  const toast = useToast();

  const set = (k: keyof NewProject) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!form.client_id) next.client_id = 'Select a client';
    if (!form.name.trim()) next.name = 'Project name is required';
    const value = form.valueRupees.replace(/[₹,\s]/g, '');
    if (!value) next.valueRupees = 'Project value is required';
    else if (!/^\d+(\.\d{1,2})?$/.test(value)) next.valueRupees = 'Enter a valid amount, e.g. 8500000';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    try {
      if (existing) {
        await update.mutateAsync({ ...form, id: existing.id });
        toast.show(`${form.name} updated`);
      } else {
        await create.mutateAsync(form);
        toast.show(`${form.name} created`);
      }
      onClose();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not save the project', true);
    }
  }

  const noClients = !clientsLoading && !clients?.length;

  return (
    <Drawer
      title={existing ? 'Edit Project' : 'Add Project'}
      description={existing ? existing.name : 'Register a new construction project and site'}
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={existing ? 'Save Changes' : 'Create Project'}
      busy={create.isPending || update.isPending}
    >
      {noClients && (
        <div className="alert" style={{ marginBottom: 16 }}>
          You need at least one client registered in the directory before creating a project.
        </div>
      )}

      <div className="form-grid">
        <Field label="Client Profile" error={errors.client_id} span2>
          <Select
            value={form.client_id}
            onChange={(val) => set('client_id')({ target: { value: val } })}
            disabled={clientsLoading}
            placeholder={clientsLoading ? 'Loading clients…' : 'Choose client account'}
            options={[
              { value: '', label: clientsLoading ? 'Loading clients…' : 'Choose client account' },
              ...(clients?.map((c) => ({ value: c.id, label: c.name })) ?? []),
            ]}
            className={errors.client_id ? 'border-rose-500' : ''}
          />
        </Field>

        <Field label="Project Name" error={errors.name} span2>
          <input
            value={form.name}
            onChange={set('name')}
            aria-invalid={!!errors.name}
          />
        </Field>

        <Field label="Site Location">
          <input
            value={form.location ?? ''}
            onChange={set('location')}
          />
        </Field>

        <Field label="Target Start Date" optional>
          <input
            type="date"
            value={form.start_date ?? ''}
            onChange={set('start_date')}
          />
        </Field>

        <Field
          label="Contract Budget (₹)"
          error={errors.valueRupees}
          hint={!errors.valueRupees ? 'Enter project value in Rupees' : undefined}
        >
          <input
            value={form.valueRupees}
            onChange={set('valueRupees')}
            inputMode="decimal"
            aria-invalid={!!errors.valueRupees}
          />
        </Field>

        <Field label="Project Status">
          <Select
            value={form.status}
            onChange={(val) => set('status')({ target: { value: val as ProjectStatus } })}
            options={STATUSES.map((s) => ({ value: s.value, label: s.label }))}
          />
        </Field>

        <Field label="Project Notes & Scope" optional span2>
          <textarea
            value={form.description ?? ''}
            onChange={set('description')}
          />
        </Field>
      </div>
    </Drawer>
  );
}
